import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Button,
  Avatar,
  Fade
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import RMALandingPage from './pages/RMALandingPage';

const gridBackgroundStyles = {
  backgroundColor: 'rgba(111, 136, 106, 1.0)',
  color: 'text.primary'
};

const LoadingSpinner = ({ message = "Loading..." }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    sx={gridBackgroundStyles}
  >
    <Fade in timeout={800}>
      <Paper
        elevation={2}
        sx={{
          p: 6,
          borderRadius: 4,
          textAlign: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          maxWidth: 400,
          width: '100%'
        }}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{ mb: 3, color: 'primary.main' }}
        />
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          {message}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we set up your session...
        </Typography>
      </Paper>
    </Fade>
  </Box>
);

const LoginPage = ({ onLogin, error = null }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    sx={gridBackgroundStyles}
  >
    <Fade in timeout={800}>
      <Paper
        elevation={2}
        sx={{
          p: 6,
          borderRadius: 4,
          textAlign: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          maxWidth: 400,
          width: '100%',
          mx: 2
        }}
      >
        <Avatar
          sx={{
            bgcolor: error ? 'error.main' : '#126F42',
            width: 72,
            height: 72,
            mx: 'auto',
            mb: 3
          }}
        >
          {error ? <WarningIcon sx={{ fontSize: 36 }} /> : <LoginIcon sx={{ fontSize: 36 }} />}
        </Avatar>

        <Typography variant="h5" gutterBottom fontWeight="bold" color="text.primary">
          {error ? 'Authentication Issue' : 'Welcome Back'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left', borderRadius: 2 }}>
            <Typography variant="body2">
              {error.message || 'Authentication failed. Please try again.'}
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', px: 2 }}>
          {error ? 'Please try signing in again' : 'Please sign in to access your RMA dashboard'}
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={onLogin}
          startIcon={<LoginIcon />}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 'bold',
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: 2
          }}
        >
          {error ? 'Try Again' : 'Sign In'}
        </Button>

        <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.disabled' }}>
          You'll be redirected to the secure login page
        </Typography>
      </Paper>
    </Fade>
  </Box>
);

const ErrorDisplay = ({ error, onRetry }) => (
  <Box sx={gridBackgroundStyles}>
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        <Fade in timeout={800}>
          <Paper
            elevation={2}
            sx={{
              p: 6,
              borderRadius: 4,
              textAlign: 'center',
              backgroundColor: '#ffffff',
              border: '1px solid',
              borderColor: 'divider',
              width: '100%'
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'error.main',
                width: 72,
                height: 72,
                mx: 'auto',
                mb: 3
              }}
            >
              <ErrorIcon sx={{ fontSize: 36 }} />
            </Avatar>

            <Typography variant="h5" gutterBottom fontWeight="bold" color="error.main">
              Authentication Error
            </Typography>

            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              We encountered an issue while trying to authenticate your session.
            </Typography>

            <Alert
              severity="error"
              sx={{
                mb: 4,
                textAlign: 'left',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" component="div">
                <strong>Error Details:</strong>
                <br />
                {error.message || 'Unknown authentication error'}
                {error.error && (
                  <>
                    <br />
                    <strong>Error Code:</strong> {error.error}
                  </>
                )}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={onRetry}
                startIcon={<RefreshIcon />}
                sx={{
                  px: 3,
                  py: 1.2,
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = window.location.origin;
                }}
                sx={{
                  px: 3,
                  py: 1.2,
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                Reset & Reload
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Box>
    </Container>
  </Box>
);

const App = () => {
  const auth = useAuth();
  const hasTriedSignin = useRef(false);
  const [authError, setAuthError] = useState(null);

  const isLoggedOut = sessionStorage.getItem('user_logged_out') === 'true' ||
                     localStorage.getItem('user_logged_out') === 'true' ||
                     new URLSearchParams(window.location.search).get('logout') === 'true';

  const handleLogin = async () => {
    try {
      setAuthError(null);
      sessionStorage.removeItem('user_logged_out');
      localStorage.removeItem('user_logged_out');
      const url = new URL(window.location);
      url.searchParams.delete('logout');
      window.history.replaceState({}, document.title, url.toString());
      await auth.signinRedirect();
    } catch (error) {
      console.error('Sign-in redirect failed:', error);
      setAuthError(error);
    }
  };

  useEffect(() => {
    if (auth.error) {
      console.error('Authentication error:', auth.error);
      setAuthError(auth.error);
    }
  }, [auth.error]);

  useEffect(() => {
    if (isLoggedOut) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      const errorDescription = urlParams.get('error_description');
      console.error('Authentication error in URL:', error, errorDescription);
      setAuthError({
        message: errorDescription || error,
        error: error
      });
      return;
    }

    if (code && state) {
      return;
    }

    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !hasTriedSignin.current) {
      hasTriedSignin.current = true;
      handleLogin();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, isLoggedOut]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      sessionStorage.removeItem('user_logged_out');
      localStorage.removeItem('user_logged_out');
      const url = new URL(window.location);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      url.searchParams.delete('logout');
      window.history.replaceState({}, document.title, url.toString());
    }
  }, [auth.isAuthenticated, auth.user]);

  if ((isLoggedOut || authError) && !auth.isAuthenticated) {
    return <LoginPage onLogin={handleLogin} error={authError} />;
  }

  if (auth.isLoading) {
    return <LoadingSpinner message="Authenticating..." />;
  }

  if (auth.error && !authError) {
    const handleRetry = () => {
      hasTriedSignin.current = false;
      setAuthError(null);
      sessionStorage.removeItem('user_logged_out');
      localStorage.removeItem('user_logged_out');
      window.history.replaceState({}, document.title, window.location.pathname);
      auth.removeUser();
      window.location.reload();
    };

    return <ErrorDisplay error={auth.error} onRetry={handleRetry} />;
  }

  if (auth.isAuthenticated && auth.user) {
    const user = {
      email: auth.user?.profile?.email,
      name: auth.user?.profile?.name || auth.user?.profile?.given_name || auth.user?.profile?.email,
      cognitoId: auth.user?.profile?.sub,
      user_type: auth.user?.profile?.['custom:user_type'] || 'customer'
    };

    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RMALandingPage user={user} />} />
          <Route path="/rma/:rmaId" element={<RMALandingPage user={user} />} />
          <Route path="/rma/:rmaId/edit" element={<RMALandingPage user={user} />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return <LoadingSpinner message="Setting up your session..." />;
};

export default App;